import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { authService } from 'components/api/auth';
import { AuthState, LoginPayload, VerifyTwoFactorPayload } from 'types/auth';

const wasAuthenticated = localStorage.getItem('isAuthenticated') === 'true';

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: wasAuthenticated,
  error: null,
  twoFactor: null,
  isTwoFactorOpen: false,
  twoFactorError: null,
  isVerifying: false,
};

// Async thunk for logging in an existing user
export const loginUser = createAsyncThunk(
  'auth/login',
  async (credentials: LoginPayload, { rejectWithValue }) => {
    try {
      const response = await authService.loginUser(credentials);
      return response;
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message || err.message || 'An error occurred during login';
      return rejectWithValue(errorMessage);
    }
  },
);

// Second login step — the authenticator code. This is what actually opens the
// session; loginUser above only proves the password.
export const verifyTwoFactorCode = createAsyncThunk(
  'auth/verifyTwoFactor',
  async (payload: VerifyTwoFactorPayload, { rejectWithValue }) => {
    try {
      const response = await authService.verifyTwoFactor(payload);
      return response;
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message || err.message || 'Could not verify the code';
      return rejectWithValue(errorMessage);
    }
  },
);

// Async thunk for logging out the user
export const logoutUser = createAsyncThunk('auth/logout', async (_, { rejectWithValue }) => {
  try {
    await authService.logoutUser();
  } catch (err: any) {
    return rejectWithValue('Logout failed');
  }
});

// Async thunk for fetching the current authenticated user session
export const fetchCurrentUser = createAsyncThunk('auth/fetchMe', async (_, { rejectWithValue }) => {
  try {
    const response = await authService.fetchCurrentUser();
    return response;
  } catch (err: any) {
    const errorMessage =
      err.response?.data?.message || err.message || 'Failed to fetch user session';
    return rejectWithValue(errorMessage);
  }
});

// Auth Slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    /**
     * Hides the dialog but keeps the challenge, so the login screen can offer
     * "Continue setup" and the admin doesn't have to retype their password
     * after closing it by accident.
     */
    closeTwoFactor: (state) => {
      state.isTwoFactorOpen = false;
      state.twoFactorError = null;
    },
    openTwoFactor: (state) => {
      if (state.twoFactor) state.isTwoFactorOpen = true;
    },
    /** Drops the challenge entirely — used when the admin abandons the login. */
    clearTwoFactor: (state) => {
      state.twoFactor = null;
      state.isTwoFactorOpen = false;
      state.twoFactorError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login cases
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false;

        const responseData = action.payload.data;

        if (!responseData) return;

        // The password step never authenticates on its own — it returns a
        // two-factor challenge, and `verifyTwoFactorCode` finishes the login.
        // `requiresTwoFactorSetup` means this admin has not enrolled yet and
        // the payload carries the QR to scan.
        if (responseData.requiresTwoFactorSetup || responseData.requiresTwoFactor) {
          state.twoFactor = {
            token: responseData.twoFactorToken,
            mode: responseData.requiresTwoFactorSetup ? 'setup' : 'verify',
            secret: responseData.secret,
            otpauthUrl: responseData.otpauthUrl,
            qrDataUrl: responseData.qrDataUrl,
          };
          state.isTwoFactorOpen = true;
          state.twoFactorError = null;
          return;
        }

        state.isAuthenticated = true;
        state.user = {
          ...responseData,
        };
        localStorage.setItem('isAuthenticated', 'true');
      })
      // Verify cases — the step that actually creates the session.
      .addCase(verifyTwoFactorCode.pending, (state) => {
        state.isVerifying = true;
        state.twoFactorError = null;
      })
      .addCase(verifyTwoFactorCode.fulfilled, (state, action) => {
        state.isVerifying = false;

        const responseData = action.payload.data;

        if (responseData) {
          state.isAuthenticated = true;
          state.user = {
            ...responseData,
          };
          state.twoFactor = null;
          state.isTwoFactorOpen = false;
          localStorage.setItem('isAuthenticated', 'true');
        }
      })
      .addCase(verifyTwoFactorCode.rejected, (state, action) => {
        state.isVerifying = false;
        // The challenge stays put: a wrong code just means "try again", and
        // dropping it here would send the admin back to retype the password.
        state.twoFactorError = action.payload as string;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.error = action.payload as string;
        // A failed password step invalidates any challenge left over from a
        // previous attempt — that token belongs to the earlier credentials.
        state.twoFactor = null;
        state.isTwoFactorOpen = false;
      })
      .addCase(logoutUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.isLoading = false;
        state.user = null;
        state.isAuthenticated = false;
        state.error = null;
        localStorage.removeItem('isAuthenticated');
      })
      .addCase(logoutUser.rejected, (state) => {
        // Even if the server fails the logout (e.g., token already invalid),
        // we should still clear the local session.
        state.isLoading = false;
        state.user = null;
        state.isAuthenticated = false;
        state.error = null;
        localStorage.removeItem('isAuthenticated');
      })
      // Fetch Current User (Session recovery) cases
      .addCase(fetchCurrentUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        state.isLoading = false;
        const responseData = action.payload.data;

        if (responseData) {
          state.isAuthenticated = true;
          state.user = {
            ...responseData,
          };
          localStorage.setItem('isAuthenticated', 'true');
        }
      })
      .addCase(fetchCurrentUser.rejected, (state) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        localStorage.removeItem('isAuthenticated');
      });
  },
});

export const { clearError, closeTwoFactor, openTwoFactor, clearTwoFactor } = authSlice.actions;

export default authSlice.reducer;
